import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useQueries, useQuery } from "react-query";
import styled from "styled-components";
import { getMovie, IGetMoviesResult } from "../api/movies";
import { IMovieSliderProps } from "../types";
import { makeImagePath } from "../utils";
import {
  IoIosArrowBack,
  IoIosArrowForward,
  IoIosArrowDown,
} from "react-icons/io";
import { useHistory } from "react-router-dom";

const PaginationBtn = styled.div<{ position: "prev" | "next" }>`
  opacity: 0;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  height: 8.5vw;
  width: 45px;
  border-radius: 3px;
  right: ${(props) => (props.position === "next" ? 0 : "auto")};
  left: ${(props) => (props.position === "prev" ? 0 : "auto")};
  &:hover {
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0.8);
    transition-duration: 0.3s;
    svg {
      scale: 1.3;
      transition-duration: 0.3s;
    }
  }
`;

const Slider = styled.div`
  position: relative;
  margin-bottom: 12vw;
  &:hover {
    ${PaginationBtn} {
      opacity: 1;
    }
  }
`;

const BoxImg = styled.img`
  width: 100%;
  height: 8.5vw;
  display: block; // to remove the white space under the image
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
`;

const NoBoxImg = styled.div`
  width: 100%;
  height: 8.5vw;
  /* display: block; // to remove the white space under the image */
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: black;
  h4 {
    font-size: 0.7vw;
  }
  div {
    font-size: 0.6vw;
  }
`;

const InfoBtn = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  margin: 10px;
  display: none;
  border-radius: 50%;
  width: 1.7vw;
  height: 1.7vw;
  background-color: #2a2a2a;
  border: 2px solid white;
`;

const Box = styled(motion.div)`
  background-color: transparent;
  font-size: 66px;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  &:first-child {
    div {
      transform-origin: center left;
    }
  }
  &:last-child {
    div {
      transform-origin: center right;
    }
  }
  &:hover ${BoxImg} {
    transition-delay: 0.5s;
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
  }
`;

const InfoContainer = styled(motion.div)`
  border-radius: 5px;
  &:hover {
    position: relative;
  }
  &:hover ${InfoBtn} {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
`;

const Info = styled(motion.div)`
  padding: 15px 10px 10px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  background-color: #181818;
  opacity: 0;
  width: 100%;
  bottom: 0;
  h4 {
    text-align: left;
    font-size: 0.8vw;
    margin-bottom: 0.5vw;
  }
  h5 {
    margin-bottom: 0.5vw;
  }
`;

const InfoMatchRating = styled.h5`
  text-align: left;
  font-size: 0.6vw;
  font-weight: bold;
  color: #47d369;
`;

const InfoRunningTime = styled.h5`
  text-align: left;
  font-size: 0.6vw;
  font-weight: bold;
  color: white;
`;

const RowHeader = styled.div`
  padding: 25px 50px;
`;

const RowHeaderTitle = styled.h1`
  color: white;
  font-size: 1.4vw;
  font-weight: 600;
  letter-spacing: 0.05rem;
`;

const Row = styled(motion.div)`
  padding: 0 50px;
  display: grid;
  gap: 5px;
  grid-template-columns: repeat(6, 1fr);
  position: absolute;
  width: 100%;
`;

const rowVariants = {
  enter: ({ isNext, width }: { isNext: boolean; width: number }) => {
    return {
      x: isNext ? width : -width,
    };
  },
  visible: {
    x: 0,
  },
  exit: ({ isNext, width }: { isNext: boolean; width: number }) => {
    return {
      x: isNext ? -width : width,
    };
  },
};

const boxVariants = {
  normal: {
    scale: 1,
  },
  hover: {
    scale: 1.3,
    y: -80,
    boxShadow: "0px 0px 15px 2px black",
    backgroundColor: "#181818",
    transition: {
      delay: 0.5,
      type: "tween",
    },
  },
};

const infoVariants = {
  hover: {
    opacity: 1,
    transition: {
      delay: 0.7,
      type: "tween",
    },
  },
};

const offset = 6;

function MovieSlider({
  title,
  setSelectedMovieId,
  queryFn,
  queryKey,
  isPartOfBanner = false,
}: IMovieSliderProps) {
  const width = window.innerWidth;
  const history = useHistory();
  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const [isNext, setIsNext] = useState(false);
  const [movieIdsInRow, setMovieIdsInRow] = useState<number[]>([]);

  const { data } = useQuery<IGetMoviesResult>({
    queryKey: queryKey,
    queryFn: queryFn,
    onSuccess: (data) => {
      const rowData = data?.results
        .slice(1)
        .slice(offset * index, offset * index + offset);
      const movieIds = rowData?.map((movie) => movie.id);
      if (movieIds) setMovieIdsInRow(movieIds);
    },
  });

  const onBoxClicked = (title: string, movieId: number) => {
    setSelectedMovieId(`${title}_${movieId}`);
    history.push(`/movie/${movieId}`);
  };

  const toggleLeaving = () => setLeaving((prev) => !prev);

  const changeIndex = (next: boolean) => {
    if (data) {
      if (leaving) return;
      setLeaving(true);
      setIsNext(next);
      const totalMovies = data.results.length - 1;
      const maxIndex = Math.floor(totalMovies / offset) - 1;
      if (next) {
        setIndex((prev) => {
          return prev === maxIndex ? 0 : prev + 1;
        });
      } else {
        setIndex((prev) => {
          return prev === 0 ? maxIndex : prev - 1;
        });
      }
    }
  };

  const QueriesByMovieId: readonly any[] = movieIdsInRow.map((movieId) => {
    return {
      queryKey: ["movieInRow", movieId],
      queryFn: () => getMovie(movieId),
    };
  });

  const moviesInRowQueries = useQueries(QueriesByMovieId);

  const getGenres = (index: number) => {
    if (moviesInRowQueries.length > 0 && !moviesInRowQueries[index].isLoading) {
      const data: any = moviesInRowQueries[index].data;
      if (data.genres && data.genres.length > 0) {
        return data.genres.map((genre: any) => genre.name).join(" · ");
      } else return "N/A";
    }
  };

  const getMovieInfoComponents = (data: IGetMoviesResult | undefined) => {
    return data?.results
      .slice(Number(isPartOfBanner))
      .slice(offset * index, offset * index + offset)
      .map((movie, index) => (
        <Box key={`${title}_${movie.id}`} layoutId={`${title}_${movie.id}`}>
          <InfoContainer
            whileHover="hover"
            initial="normal"
            variants={boxVariants}
            transition={{ type: "tween" }}
            onClick={() => {
              onBoxClicked(title, movie.id);
            }}
            custom={{ isNext, width }}
          >
            {movie.backdrop_path ? (
              <BoxImg src={makeImagePath(movie.backdrop_path, "w500")} />
            ) : (
              <NoBoxImg>
                <h4>
                  Title: <span style={{ fontWeight: 600 }}>{movie.title}</span>
                </h4>
                <div>No image available.</div>
              </NoBoxImg>
            )}
            <InfoBtn>
              <IoIosArrowDown color="white" />
            </InfoBtn>
            <Info variants={infoVariants}>
              <h4>{movie.title}</h4>
              <InfoMatchRating>
                {movie.vote_average * 10}% Match
              </InfoMatchRating>
              <InfoRunningTime>{getGenres(index)}</InfoRunningTime>
            </Info>
          </InfoContainer>
        </Box>
      ));
  };

  useEffect(() => {
    const rowData = data?.results
      .slice(1)
      .slice(offset * index, offset * index + offset);
    const movieIds = rowData?.map((movie) => movie.id);
    if (movieIds) setMovieIdsInRow(movieIds);
  }, [data, index]);

  return (
    <Slider>
      <RowHeader>
        <RowHeaderTitle>{title}</RowHeaderTitle>
      </RowHeader>
      <AnimatePresence
        initial={false}
        custom={{ isNext, width }}
        onExitComplete={toggleLeaving}
      >
        <Row
          key={`${title}_${index}`}
          variants={rowVariants}
          initial="enter"
          animate="visible"
          exit="exit"
          custom={{ isNext, width }}
          transition={{
            x: { type: "tween", duration: 1 },
            opacity: { duration: 1 },
          }}
        >
          {getMovieInfoComponents(data)}
        </Row>
      </AnimatePresence>
      <PaginationBtn
        key="prev"
        position="prev"
        onClick={() => changeIndex(false)}
      >
        <IoIosArrowBack size={35} />
      </PaginationBtn>
      <PaginationBtn
        key="next"
        position="next"
        onClick={() => changeIndex(true)}
      >
        <IoIosArrowForward size={35} />
      </PaginationBtn>
    </Slider>
  );
}

export default MovieSlider;
